import React from 'react';

import { useMutation, useApolloClient } from '@apollo/client';
import { MUTATION } from '../gql-operations';

import { AuthContext } from '../util/context';
import { CacheManager } from '../util/cacheManager';
import customAnalytics from '../util/custom-analytics';

/**
 * Hook for restoring messages from trash
 * Uses centralized CacheManager for cache updates
 */
function useRestoreMessagesMutation() {
    const client = useApolloClient();
    const [restoreMessage] = useMutation(MUTATION.RESTORE_MESSAGES, {
        onCompleted: res => {
            // analytics at the bottom
        }
    });

    const {
        state: {
            folderFilterType,
            selectedFolder,
            showDeletedMessages,
            searchTerm,
            searchGlobal,
            searchDeletedMessages,
            searchFolderFilterType,
            searchActive
        }
    } = React.useContext(AuthContext);
    const normalizedSearchTerm = String(searchTerm || '').trim();

    /**
     * Extract message IDs for the mutation
     * Filters out temp/optimistic messages that don't exist on server yet
     */
    const prepareMessages = messages => {
        return messages
            .filter(m => !m.id?.startsWith('temp-'))
            .map(message => message.id);
    };

    /**
     * Perform cache update for message restoration
     */
    const performCacheUpdate = (messages, fromFolder) => {
        // When restoring, we're viewing trash (isTrash = true)
        // and we need to remove messages from the trash view
        const isTrash = true; // Always true when restoring - we're in trash view

        // Use CacheManager to remove messages from trash caches
        CacheManager.removeMessages(client, messages, {
            folderId: selectedFolder?.id,
            isTrash: isTrash,
            filterType: folderFilterType,
            isSearch: searchActive,
            searchTerm: normalizedSearchTerm,
            searchGlobal: searchGlobal,
            searchFolderFilterType: searchFolderFilterType
        });

        // Dispatch event to update memory refs in containers
        CacheManager.dispatchEvent('messagesRestored', {
            messageIds: messages.map(m => m.id),
            fromFolderId: fromFolder?.id || selectedFolder?.id
        });
    };

    /**
     * Main function to restore messages
     */
    const restoreMessagesMutation = (messages, fromFolder) => {
        if (process.env.NODE_ENV === 'development') {
            console.log('🔄 useRestoreMessages: Restoring', messages.length, 'messages');
        }

        // Optimistically update cache IMMEDIATELY for instant UI feedback
        try {
            performCacheUpdate(messages, fromFolder);
        } catch (optimisticError) {
            if (process.env.NODE_ENV === 'development') {
                console.log('Optimistic update failed, will rely on mutation update callback:', optimisticError);
            }
        }

        const messageIds = prepareMessages(messages);
        
        // If all messages were temp messages (filtered out), nothing to send to server
        if (messageIds.length === 0) {
            if (process.env.NODE_ENV === 'development') {
                console.log('🔄 useRestoreMessages: All messages are temp, skipping server mutation');
            }
            return;
        }
        
        // Now call the mutation
        restoreMessage({
            variables: {
                data: {
                    messageIds: messageIds
                }
            },
            update: (store, { data: { restoreMessages } }) => {
                try {
                    // Run cache update again to ensure consistency after server response
                    performCacheUpdate(messages, fromFolder);
                    
                    if (process.env.NODE_ENV === 'development') {
                        console.log('✅ Restore mutation completed, cache updated');
                    }
                } catch (error) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error('Cache update failed during restore:', error);
                    }
                }
            },
            onError: (error) => {
                // If mutation fails, log error
                if (process.env.NODE_ENV === 'development') {
                    console.error('Restore mutation failed:', error);
                }
            }
        });

        customAnalytics.restore_messages(messages);
    };

    return { restoreMessagesMutation };
}

export default useRestoreMessagesMutation;
